import { useState } from 'react';
import { EmailMessageThreadClient, EmailMessageThreadDTO, ApiResponse_string } from '../services/apiClient';
import { API_BASE_URL } from '../config/config';
import axiosInstance from '../services/axiosInstance';
import { handleHookError } from '../utils/hookUtils/handleHookError';

export const useEmailMessageThreadApi = () => {
    const apiClient = new EmailMessageThreadClient(API_BASE_URL, axiosInstance);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const create = async (data: EmailMessageThreadDTO) => {
        setIsLoading(true);
        try {
            await apiClient.createEmailMessageThread(data);
        } catch (err: any) {
            setError(err.message || 'Error creating EmailMessageThread');
        } finally {
            setIsLoading(false);
        }
    };

    const edit = async (id: string, data: EmailMessageThreadDTO) => {
        setIsLoading(true);
        try {
            await apiClient.updateEmailMessageThread(id, data);
        } catch (err: any) {
            setError(err.message || 'Error updating EmailMessageThread');
        } finally {
            setIsLoading(false);
        }
    };

    const getById = async (id: string) => {
        setIsLoading(true);
        try {
            return await apiClient.getEmailMessageThreadById(id);
        } catch (err: any) {
            setError(err.message || 'Error fetching EmailMessageThread');
        } finally {
            setIsLoading(false);
        }
    };

    const search = async (filters: any) => {
        setIsLoading(true);
        try {
            return await apiClient.searchEmailMessageThreads(filters);
        } catch (err: any) {
            setError(err.message || 'Error searching EmailMessageThreads');
        } finally {
            setIsLoading(false);
        }
    };

    const remove = async (id: string) => {
        setIsLoading(true);
        try {
            await apiClient.deleteEmailMessageThread(id);
        } catch (err: any) {
            setError(err.message || 'Error deleting EmailMessageThread');
        } finally {
            setIsLoading(false);
        }
    };

    const extract = async (
        filters: any
    ): Promise<ApiResponse_string> => {
        setIsLoading(true);
        try {
            return await apiClient.exportEmailMessageThreads(filters);
        } catch (err: unknown) {
            handleHookError(err, 'Error exporting EmailMessageThreads', setError);
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const markAsReplied = async (id: string) => {
        setIsLoading(true);
        try {
            return await apiClient.markEmailMessageThreadAsReplied(id);
        } catch (err: any) {
            setError(err.message || 'Error marking EmailMessageThread as replied');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const markAsResolved = async (id: string) => {
        setIsLoading(true);
        try {
            return await apiClient.markEmailMessageThreadAsResolved(id);
        } catch (err: any) {
            setError(err.message || 'Error marking EmailMessageThread as resolved');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const setRating = async (threadId: string, rating: number) => {
        setIsLoading(true);
        try {
            return await apiClient.setEmailMessageThreadRating(threadId, rating);
        } catch (err: any) {
            setError(err.message || 'Error setting EmailMessageThread rating');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    return { create, edit, getById, search, extract, remove, markAsReplied, markAsResolved, setRating, isLoading, error };
};